/**
 * The closing net / VAT / gross block on a payment preview, shared by the
 * production and sample pay modals.
 *
 * Both modals used to print `grandTotal` under the label "Total (ex VAT)" — true,
 * but not the number the customer is charged. The amount that reaches the card or
 * the invoice is `grossAmount`, so the total shown here is gross and the net sits
 * above it as the subtotal it actually is.
 *
 * Not `CurrencyText`: a payment's currency is fixed by its method (Stripe charges
 * EUR, invoice DKK), which is exactly what the global currency selector must not
 * override.
 */
export const money = (amount: number) =>
	amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PaymentTotalsProps {
	currency: string;
	/** Net total, ex-VAT. */
	grandTotal: number;
	vatRate: number;
	vatAmount: number;
	/** grandTotal + vatAmount — what will actually be charged. */
	grossAmount: number;
}

export function PaymentTotals({
	currency,
	grandTotal,
	vatRate,
	vatAmount,
	grossAmount,
}: PaymentTotalsProps) {
	return (
		<div className="px-4 py-3">
			<div className="flex items-center justify-between py-1 text-sm">
				<span className="text-text-3">Subtotal</span>
				<span className="text-text-2">
					{currency} {money(grandTotal)}
				</span>
			</div>
			<div className="flex items-center justify-between py-1 text-sm">
				<span className="text-text-3">
					VAT ({vatRate}%)
					{/* A zero rate is a decision (reverse charge, export), not a missing
					    number — saying which keeps it from reading as a bug. */}
					{vatRate === 0 && (
						<span className="ml-1 text-2xs text-text-4">reverse charge / export</span>
					)}
				</span>
				<span className="text-text-2">
					{currency} {money(vatAmount)}
				</span>
			</div>
			<div className="mt-1 flex items-center justify-between border-t border-line-1 pt-2">
				<span className="text-sm font-medium text-text-1">Total</span>
				<span className="text-right">
					<span className="block text-base font-medium text-text-1">
						{currency} {money(grossAmount)}
					</span>
					<span className="mt-0.5 block text-2xs text-text-4">incl. VAT</span>
				</span>
			</div>
		</div>
	);
}
